import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";

/* Cloudflare Turnstile widget (Launch readiness — Workstream E).
   Renders the challenge and reports the token via onVerify (null on
   expiry/error). Renders NOTHING when VITE_TURNSTILE_SITE_KEY is unset, so
   local dev without a key — and any build before Supabase captcha is switched
   on — works unchanged. Pass a stable onVerify (e.g. a useState setter).
   Expose reset() so the caller can get a fresh token after each auth attempt;
   Turnstile tokens are single-use and consumed by Supabase on each call.

   onError fires when the Cloudflare script fails to load (ad blocker,
   network filter) or times out — without it the submit button just stays
   disabled forever with no explanation. The parent shows a message and can
   retry by remounting (key bump); the failed script promise is cleared so a
   retry actually re-attempts the load. */

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;
const LOAD_TIMEOUT_MS = 12000;
function loadScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    const fail = (err) => {
      clearTimeout(timer);
      scriptPromise = null; // let a retry re-attempt the load
      s.remove();
      reject(err);
    };
    const timer = setTimeout(() => fail(new Error("turnstile load timeout")), LOAD_TIMEOUT_MS);
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    s.onerror = () => fail(new Error("turnstile script failed"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

const Turnstile = forwardRef(function Turnstile({ onVerify, onError }, ref) {
  const elRef = useRef(null);
  const widgetId = useRef(null);

  /* The widget must render EXACTLY ONCE per mount. The old effect was keyed
     on [onVerify, onError], so an unstable callback prop (an inline arrow in
     the parent) tore the widget down and re-rendered it on every parent
     render — with an interactive challenge that reads as a flickering,
     spinning, never-completing check, worse on every keystroke. Callbacks
     now live in refs (kept current each render via an effect, per the
     react-hooks/refs rule) and the render effect runs on mount only, so
     callback identity can never remount the widget. The parent's key bump
     remains the one intentional remount path (retry after load failure). */
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
  });

  useImperativeHandle(ref, () => ({
    reset() {
      if (window.turnstile && widgetId.current != null) {
        window.turnstile.reset(widgetId.current);
      }
    },
  }));

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !elRef.current || widgetId.current != null) return;
        widgetId.current = window.turnstile.render(elRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onVerifyRef.current?.(token),
          "expired-callback": () => onVerifyRef.current?.(null),
          "error-callback": () => onVerifyRef.current?.(null),
        });
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current?.();
      });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetId.current != null) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* ignore */
        }
        widgetId.current = null;
      }
    };
  }, []); // mount-once by design — see comment above

  if (!SITE_KEY) return null;
  return <div className="cf-turnstile-host" ref={elRef} />;
});

export default Turnstile;
